import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { LoginService } from '@shared-angular/services/login.service';
import { RagLoggerService } from '@shared-angular/services/rag-logger/rag-logger.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { ChatbotConfig, isIdleState, MessageContent } from '@shared/index';
import { BsModalService } from 'ngx-bootstrap/modal';
import { DisconnectOverlayComponent } from 'src/app/shared/components/disconnect-overlay/disconnect-overlay.component';
import { MicPermissionOverlayComponent } from 'src/app/shared/components/mic-permission-overlay/mic-permission-overlay.component';
import { AudioManagerService } from 'src/app/shared/services/audio-manager.service';
import { AudioToTextService } from 'src/app/shared/services/audio-to-text/audio-to-text.service';
import { BotMessagesService } from 'src/app/shared/services/bot-messages.service';
import { BotService } from 'src/app/shared/services/bot.service';
import { EventManagerService } from 'src/app/shared/services/event-manager/event-manager.service';
import { IframeCommunicationService } from 'src/app/shared/services/iframe-communication/iframe-communication.service';
import { OverlayManagerService } from 'src/app/shared/services/overlay-manager.service';
import { PlayAudioService } from 'src/app/shared/services/playaudio.service';
import { SocketService } from 'src/app/shared/services/socket.service';
import { SpeechService } from 'src/app/shared/services/speech.service';
import { StatisticService } from '../admin/tabs/insights/statistics-tab/statistic.service';
import { BotBasedComponent } from '../bot-based/bot-based.component';
import { AudioDebuggerComponent } from "./audio-debugger/audio-debugger.component";

@Component({
  selector: 'app-audionew',
  templateUrl: './audionew.component.html',
  styleUrl: './audionew.component.scss',
  imports: [NgClass, NgStyle, NgTemplateOutlet, DisconnectOverlayComponent, MicPermissionOverlayComponent, AudioDebuggerComponent],
  providers: [BsModalService]
})
export class AudionewComponent extends BotBasedComponent implements OnInit, OnDestroy {
  private lastSpokenMessageId = '';
  constructor(
    public override route: ActivatedRoute,
    public override loginService: LoginService,
    public override speechService: SpeechService,
    public override statisticsService: StatisticService,
    public override iframeCommunication: IframeCommunicationService,
    public override playService: PlayAudioService,
    public override bot: BotService,
    public override socketService: SocketService,
    public override toastr: ToastrService,
    public override modalService: BsModalService,
    public override domSanatizer: DomSanitizer,
    protected override botMessages: BotMessagesService,
    public override logger: RagLoggerService,
    public attService: AudioToTextService,
    private audioManager: AudioManagerService,
    private eventManager: EventManagerService,
    protected override overlayManager: OverlayManagerService
  ) {
    super(route, loginService, speechService, statisticsService, iframeCommunication, playService, bot, socketService, toastr, modalService, domSanatizer, botMessages, logger, overlayManager);
    this.showMcpApprovalOverlayEnabled = true;

    route.data.subscribe(data => {
      const botConfig = data['botConfig'] as ChatbotConfig | null;
      if (!botConfig) return;
      this.botConfig = botConfig;
    });

    this.subscriptions.push(
      this.socketService.onRunComplete.subscribe((event) => {
        // Keine Sprachausgabe fuer MCP-Rollen (Approval-Request/-Response/Call) - nur echte Bot-Antworten vorlesen
        if (event.role !== 'assistant') return;
        this.playBotWords(this.getMessageContent(event.content));
      }),
      this.socketService.onStateUpdate.subscribe(async (data) => {
        if (this.botMessages.getMessageCount() === 0) {
          if (data.sessionState === "idle") {
            this.attService.startRecording();
          }
        }

        if (isIdleState(data.sessionState)) {
          if (this.prevBotState != "unknown") {
            const candidateMsg = this.botMessages.findLatestAssistantMessage();
            if (candidateMsg && candidateMsg._id != this.lastSpokenMessageId) {
              this.lastSpokenMessageId = candidateMsg._id;
              await this.broadCastMSG(candidateMsg);
              this.iframeCommunication.runComplete(candidateMsg);
              this.eventManager._trigger_RAGAI_EVENT("RAGAI.AUDIO.SPEECH.repeatlastmessage", { message: candidateMsg.content });
              this.playBotWords(this.getMessageContent(candidateMsg.content));
            }
          }
          this.attService.state.next('listening');
          this.isThinking = false;
        } else {
          this.isThinking = true;
        }

        this.prevBotState = data.sessionState;
        this.attService.state.next("thinking");
      }),
      this.socketService.onLogoutOldSession.subscribe((data) => {
        if (this.loginService.loggedInUser && this.loginService.loggedInUser._id === data.oldSessionId) {
          void this.loginService.logout(false);
        }
      }),
      this.socketService.onCreateMessagesInThread.subscribe((data) => {
        const cards = data.messages.filter(m => m.role == 'botcard');
        if (cards.length == 1 && cards[0]) {
          this.openCardAsModal(cards[0].content);
        }
      })
    );
  }

  public playBotWords(text: string) {
    void this.playService.playAudio(text, this.botConfig._id, this.botConfig.voiceConfig.id);
  }

  public getOverWriteText(): string {
    return window.audiobot_overrideText ?? "";
  }

  public override async onMessagReceived(_isFirst: boolean) {
    // No-op: AudionewComponent handles runComplete in its own onStateUpdate subscription
  }

  public override async ngOnInit() {
    this.attService.state.subscribe((state) => {
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.AUDIO.SPEECH." + state, { state });
    });

    this.attService.textReceived.subscribe((text) => {
      void this.add(text);
    });

    this.attService.recStarted.subscribe((status) => {
      if (status) {
        if (this.playService.state === "play") {
          this.playService.stopAudio();
        }
      }
    });

    this.playService.currentAudioStatus.subscribe((status) => {
      // console.log("AudioStatus", status);
      if (status[0] === "play") {
        this.attService.speakingState.next(true);
        this.attService.speechRecognitionThresholdElevationMultiplyer = 2;
        // HÖRT JETZT WIEDER ZU SOBALD DER SRTATUS "PLAY IST"
        this.attService.startRecording();
      }
      if (status[0] === "idle") {
        ///console.log("DIES HIER !!?!");
        this.attService.speakingState.next(false);
        if (this.attService.autoMute) {
          this.attService.setMuted(false);
        }
        this.attService.speechRecognitionThresholdElevationMultiplyer = 1;
      }
    });

    this.attService.state.next('disconnected');
    if (this.loginService.loggedInUser === null) {
      // persist=false: Token wird erst nach Bestaetigung des Datenschutzhinweises persistiert
      await this.loginService.loadMyUser(false);
    }
    await this.showGDPR();
    this.usertokenService.persistToken();

    const oldMessages = (await this.bot.getHistoryMessages(this.botConfig._id)).messageHistory;
    this.botMessages.setMessages(oldMessages);
    this.initIFrameService();
    if (this.botConfig as ChatbotConfig | null) {
      this.injectJs(this.botConfig._id);
      this.injectCSS(this.botConfig._id, "voice");
    }
  }

  public disconnectAudioCall() {
    window.location.reload();
  }

  protected get muteButtonLabel(): string {
    return this.attService.isMuted
      ? $localize`:@@audio-unmute:Stummschaltung aufheben`
      : $localize`:@@audio-mute:Stummschalten`;
  }

  public connectAudioCall() {
    this.attService.state.next('connecting');
    this.socketService.connectToThread(this.botConfig._id);
    if (this.isIOS()) {
      this.playService.audio.play()
        .then(() => {
          this.playService.audio.pause();
        })
        .catch((err: unknown) => {
          console.log('Audio noch blockiert, wird aber durch Interaktion gleich erlaubt', err);
        });
    }
  }

  private isIOS(): boolean {
    const ua = navigator.userAgent;
    return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
  }

  private getMessageContent(content: MessageContent[]): string {
    let m = '';
    const msg = content.at(0);
    if (msg?.type === 'text') {
      m = msg.text;
    }
    return m;
  }
}
